import { useMemo } from "react";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Message } from "primereact/message";
import * as client from "../../../client";
import { CatResultsResponse } from "../../../client/types.gen";
import { formatExactMoneyDollars } from "../Money";
import { compareLayers, readableLayer } from "../layers/util";
import { getPerilColor } from "../icm/util";
import {
  SelectedModel,
  RmsLayerResult,
  LayerPerilRow,
  getPerilDisplayName,
  isLayerTotalPeril,
} from "./types";
import styles from "./CatCard.module.css";

interface LayerPerilTabProps {
  selectedModel: SelectedModel;
  icmResultsData: CatResultsResponse | null;
  rmsResultsData: CatResultsResponse | null;
  rmsEltResultsData: CatResultsResponse | null;
  spatialKatResultsData: CatResultsResponse | null;
  layerInfoById: Map<string, client.Layer>;
}

const compareByResolvedLayer =
  (layerInfoById: Map<string, client.Layer>) =>
  <T extends { layer_id: string | null }>(a: T, b: T): number => {
    const infoA = a.layer_id ? layerInfoById.get(a.layer_id) : undefined;
    const infoB = b.layer_id ? layerInfoById.get(b.layer_id) : undefined;
    if (!infoA) return infoB ? 1 : 0;
    if (!infoB) return -1;
    return compareLayers(infoA, infoB);
  };

export const LayerPerilTab: React.FC<LayerPerilTabProps> = ({
  selectedModel,
  icmResultsData,
  rmsResultsData,
  rmsEltResultsData,
  spatialKatResultsData,
  layerInfoById,
}) => {
  const getRmsLayers = useMemo((): RmsLayerResult[] => {
    const rmsData = rmsResultsData?.rms?.aggregate;
    if (!rmsData?.layers) return [];

    return rmsData.layers
      .filter((layer) => {
        return layer.layer_guid && layer.layer_guid !== "";
      })
      .map((layer) => ({
        ...layer,
        layer_id: layer.layer_guid,
      }));
  }, [rmsResultsData]);

  const getRmsEltLayers = useMemo((): RmsLayerResult[] => {
    const eltData = rmsEltResultsData?.rms_elt?.aggregate;
    if (!eltData?.layers) return [];

    return eltData.layers
      .filter((layer) => layer.layer_guid && layer.layer_guid !== "")
      .map((layer) => ({
        ...layer,
        layer_id: layer.layer_guid,
      }));
  }, [rmsEltResultsData]);

  const getSpatialKatLayers = useMemo((): RmsLayerResult[] => {
    const skData = spatialKatResultsData?.spatialkat;
    if (!skData?.aggregate?.layers) return [];

    return skData.aggregate.layers
      .filter((layer) => layer.layer_guid && layer.layer_guid !== "")
      .map((layer) => ({
        ...layer,
        layer_id: layer.layer_guid,
      }));
  }, [spatialKatResultsData]);

  const renderPerilLabel = (perilCode: string) => {
    if (!perilCode) {
      return null;
    }
    const label = getPerilDisplayName(perilCode);
    if (isLayerTotalPeril(perilCode)) {
      return <span className={styles.fontBold}>{label}</span>;
    }

    return (
      <span className={styles.perilLabel}>
        <span
          className={styles.perilColorDot}
          style={{ backgroundColor: getPerilColor(perilCode) }}
        />
        <span>{label}</span>
      </span>
    );
  };

  const lossesByLayerPeril = useMemo((): LayerPerilRow[] => {
    if (selectedModel === "icm") {
      const icmAir = icmResultsData?.icm?.air;
      const outerLayers = icmAir?.aggregate?.layers || [];
      if (outerLayers.length === 0) return [];

      const validLayers = outerLayers.filter((layer) => {
        return layer.layer_guid && layer.layer_guid !== "";
      });

      const enrichedLayers = validLayers.map((layer) => ({
        ...layer,
        layer_id: layer.layer_guid ?? null,
      }));

      const sortedLayers = [...enrichedLayers].sort(
        compareByResolvedLayer(layerInfoById)
      );

      const rows: LayerPerilRow[] = [];

      for (const layer of sortedLayers) {
        const layerInfo = layer.layer_id
          ? layerInfoById.get(layer.layer_id)
          : undefined;
        const layerName = layerInfo
          ? readableLayer(layerInfo)
          : (layer.layer_id ?? "Unknown");

        const perilEntries = Object.entries(layer.perils || {}).sort(
          ([a], [b]) =>
            getPerilDisplayName(a).localeCompare(getPerilDisplayName(b))
        );

        let isFirst = true;
        for (const [peril, data] of perilEntries) {
          rows.push({
            layer: layerName,
            peril,
            aal: data.aal ?? null,
            pml_100: data.pml_per_period?.["100"] ?? null,
            pml_250: data.pml_per_period?.["250"] ?? null,
            pml_1000: data.pml_per_period?.["1000"] ?? null,
            isFirstInGroup: isFirst,
          });
          isFirst = false;
        }

        rows.push({
          layer: layerName,
          peril: "Combined",
          aal: layer.totals?.aal ?? null,
          pml_100: layer.totals?.pml_per_period?.["100"] ?? null,
          pml_250: layer.totals?.pml_per_period?.["250"] ?? null,
          pml_1000: layer.totals?.pml_per_period?.["1000"] ?? null,
          isFirstInGroup: isFirst,
        });
      }

      return rows;
    } else {
      const modelLayers =
        selectedModel === "spatialkat"
          ? getSpatialKatLayers
          : selectedModel === "rms_elt"
            ? getRmsEltLayers
            : getRmsLayers;
      if (modelLayers.length === 0) return [];

      const sortedLayers = [...modelLayers].sort(
        compareByResolvedLayer(layerInfoById)
      );

      const rows: LayerPerilRow[] = [];

      for (const rmsLayer of sortedLayers) {
        const layerInfo = rmsLayer.layer_id
          ? layerInfoById.get(rmsLayer.layer_id)
          : undefined;
        const layerName = layerInfo
          ? readableLayer(layerInfo)
          : (rmsLayer.layer_id ?? "Unknown");

        // RMS perils are in the perils object, but they may not have AAL values
        // For now, just show the totals
        const perilEntries = Object.entries(rmsLayer.perils || {}).sort(
          ([a], [b]) =>
            getPerilDisplayName(a).localeCompare(getPerilDisplayName(b))
        );

        // Get EP curves for this layer for PML values
        const layerEpCurves = rmsLayer.ep_curves || [];
        const grAepCurve = layerEpCurves.find(
          (c: client.EpRow) => c.perspective === "GR" && c.metric_type === "AEP"
        );
        const pml100 = grAepCurve?.return_period_values?.["100"] ?? null;
        const pml250 = grAepCurve?.return_period_values?.["250"] ?? null;
        const pml1000 = grAepCurve?.return_period_values?.["1000"] ?? null;

        let isFirst = true;
        for (const [peril, data] of perilEntries) {
          const perilData = data as client.CatPerilLayerData;
          rows.push({
            layer: layerName,
            peril,
            aal: perilData.aal ?? null,
            pml_100: perilData.pml_per_period?.["100"] ?? null,
            pml_250: perilData.pml_per_period?.["250"] ?? null,
            pml_1000: perilData.pml_per_period?.["1000"] ?? null,
            isFirstInGroup: isFirst,
          });
          isFirst = false;
        }

        // Add combined row from totals
        rows.push({
          layer: layerName,
          peril: "Combined",
          aal: rmsLayer.totals?.aal ?? null,
          pml_100: pml100,
          pml_250: pml250,
          pml_1000: pml1000,
          isFirstInGroup: isFirst,
        });
      }

      return rows;
    }
  }, [
    selectedModel,
    icmResultsData,
    rmsResultsData,
    rmsEltResultsData,
    spatialKatResultsData,
    layerInfoById,
    getRmsLayers,
    getRmsEltLayers,
    getSpatialKatLayers,
  ]);

  if (lossesByLayerPeril.length === 0) {
    return (
      <Message
        severity="info"
        text={`No ${selectedModel === "icm" ? "ICM" : selectedModel === "spatialkat" ? "KatRisk" : selectedModel === "rms_elt" ? "RMS" : "RMS (Legacy)"} data available. Run an analysis to generate results.`}
        className={styles.messageMargin}
      />
    );
  }

  return (
    <DataTable
      value={lossesByLayerPeril}
      rowGroupMode="rowspan"
      groupRowsBy="layer"
      rowClassName={(rowData: LayerPerilRow) => {
        const classes: string[] = [];
        if (isLayerTotalPeril(rowData.peril)) {
          classes.push(styles.layerTotalRow);
        }
        if (rowData.isFirstInGroup) {
          classes.push(styles.layerGroupStart);
        }
        return classes.join(" ");
      }}
    >
      <Column
        field="layer"
        header="Layer"
        style={{ minWidth: "100px" }}
        bodyClassName={styles.layerCell}
      />
      <Column
        field="peril"
        header="Peril"
        body={(rowData: LayerPerilRow) => renderPerilLabel(rowData.peril)}
        style={{ minWidth: "100px" }}
      />
      <Column
        field="aal"
        header="AAL"
        body={(rowData: LayerPerilRow) =>
          rowData.aal != null
            ? formatExactMoneyDollars(rowData.aal, {
                withRounding: true,
                withDollarSign: true,
              })
            : ""
        }
        style={{ minWidth: "120px" }}
        align="right"
      />
      <Column
        field="pml_100"
        header="100 Year PML"
        body={(rowData: LayerPerilRow) =>
          rowData.pml_100 != null
            ? formatExactMoneyDollars(rowData.pml_100, {
                withRounding: true,
                withDollarSign: true,
              })
            : ""
        }
        style={{ minWidth: "120px" }}
        align="right"
      />
      <Column
        field="pml_250"
        header="250 Year PML"
        body={(rowData: LayerPerilRow) =>
          rowData.pml_250 != null
            ? formatExactMoneyDollars(rowData.pml_250, {
                withRounding: true,
                withDollarSign: true,
              })
            : ""
        }
        style={{ minWidth: "120px" }}
        align="right"
      />
      <Column
        field="pml_1000"
        header="1000 Year PML"
        body={(rowData: LayerPerilRow) =>
          rowData.pml_1000 != null
            ? formatExactMoneyDollars(rowData.pml_1000, {
                withRounding: true,
                withDollarSign: true,
              })
            : ""
        }
        style={{ minWidth: "120px" }}
        align="right"
      />
    </DataTable>
  );
};
